import React, { useState, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button } from "react-bootstrap";
import { FiClock } from "react-icons/fi";
import pixLogo from "../../assets/pix.svg";
import { useApp } from "../../contexts/AppContext";
import { BrlFormatCurrency } from "../../lib";
import {
  PageContainer,
  PixCodeBox,
  OrderNumberBox,
  QRCodeWrapper,
  ExpirationBadge,
  Separator,
} from "./styles";

export default function PixConfirmation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { whitelabel } = useApp();
  const [copied, setCopied] = useState(false);

  const { pixCode, pixQrCode, pixExpiresAt, totalAmount } = location.state || {};

  const expirationTime = useMemo(() => {
    if (!pixExpiresAt) return "--:--";
    return new Date(pixExpiresAt).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }, [pixExpiresAt]);

  const handleCopyCode = async () => {
    if (!pixCode) return;
    try {
      await navigator.clipboard.writeText(pixCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = pixCode;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  if (!pixCode) {
    return (
      <div className="wl-page">
        <PageContainer className="text-center">
          <p
            className="font-wl-600 text-mg mt-5"
            style={{ color: whitelabel?.theme?.text_primary_color }}
          >
            Dados do pagamento Pix não encontrados.
          </p>
          <Button
            type="button"
            className="btn-default-wl text-xxs font-wl-700 mt-4"
            onClick={() => navigate("/loja")}
          >
            VOLTAR PARA A HOME
          </Button>
        </PageContainer>
      </div>
    );
  }

  return (
    <div className="wl-page">
      <PageContainer>
        {/* 1. Título */}
        <p
          className="font-wl-700 text-mg mb-3 text-center"
          style={{ color: whitelabel?.theme?.text_primary_color }}
        >
          <FiClock size={20} style={{ color: "#4CAF50" }} /> Pedido aguardando pagamento
        </p>

        {/* 2. ID do pedido + Total */}
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, marginTop: 20 }}>
          <span className="font-wl-400 text-sm" style={{ color: "#555" }}>ID do pedido:</span>
          <span className="font-wl-700 text-sm" style={{ color: whitelabel?.theme?.text_primary_color }}>{id?.substring(0, 8).toUpperCase()}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 32 }}>
          <span className="font-wl-400 text-sm" style={{ color: "#555" }}>Total a pagar</span>
          <span className="font-wl-700 text-sm" style={{ color: whitelabel?.theme?.text_primary_color }}>
            {totalAmount ? BrlFormatCurrency(totalAmount) : "--"}
          </span>
        </div>

        {/* 3. Logo Pix + Título */}
        <div className="text-center my-3" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <img
            src={pixLogo}
            alt="Pix"
            style={{ width: 28, height: 28 }}
          />
          <p
            className="font-wl-600 text-sm mb-0"
            style={{ color: whitelabel?.theme?.text_primary_color }}
          >
            Pagamento via Pix
          </p>
        </div>

        {/* 4. Expiração */}
        <ExpirationBadge>
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
            <path d="M11 1V5M11 17V21M3.93 3.93L6.76 6.76M15.24 15.24L18.07 18.07M1 11H5M17 11H21M3.93 18.07L6.76 15.24M15.24 6.76L18.07 3.93" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="text-xs font-wl-500" style={{ color: "#DC2626" }}>
            O código é válido até às {expirationTime}.
          </span>
        </ExpirationBadge>

        {/* 5. Código Pix */}
        <PixCodeBox>{pixCode}</PixCodeBox>

        {/* 6. Botão copiar */}
        <Button
          type="button"
          className="btn-default-wl text-xxs font-wl-700 w-100"
          onClick={handleCopyCode}
        >
          {copied ? "CÓDIGO COPIADO COM SUCESSO" : "COPIAR CÓDIGO PIX"}
        </Button>

        {/* 7. QR Code */}
        <QRCodeWrapper>
          <img
            src={pixQrCode}
            alt="QR Code Pix"
            style={{ width: 160, height: 160 }}
          />
        </QRCodeWrapper>

        {/* 8. Aviso cancelamento */}
        <p
          className="font-wl-400 text-xs mb-0"
          style={{ color: "#767F8D" }}
        >
          Vá até o app do seu banco e acesse a área <b>Pix Copia e Cola</b>. Cole o código, 
          confira as informações e finalize o pagamento <br /><br />
          
          Ou se preferir, escaneie o QR Code abaixo.
        </p>

        <Separator />

        {/* 10. Acompanhar pedido */}
        <div className="text-center mt-3 mb-4">
          <button
            type="button"
            onClick={() => navigate(`/loja/orderresumestatus/${id}`)}
            className="font-wl-600 text-sm"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: whitelabel?.theme?.text_primary_color,
              textDecoration: "underline",
              padding: 0,
            }}
          >
            Acompanhar pedido
          </button>
        </div>
      </PageContainer>
    </div>
  );
}
